<template>
  <b-overlay :show="loading" class="animated">
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :theme="theme"
      :ref="ref"
      id="submission-server-table"
      class="w-100"
    >
      <div slot="beforeTable" class="pt-3">
        <b-row>
          <b-col>
            <select-filter
              variant="info"
              icon="fa-toggle-on"
              placeholder="Quality Control"
              :list="[
                { value: 1, text: 'Yes - Quality Control' },
                { value: 0, text: 'No - Quality Control' },
              ]"
              :selected="show_quality_control"
              @onSelect="toggleColumn('quality_control', $event.value)"
            />
          </b-col>

          <b-col>
            <select-filter
              variant="info"
              icon="fa-shovel"
              placeholder="All Sample Types"
              :collection="sample_type_filters"
              value-key="id"
              text-key="name"
              filter-field="submissions.sample_type_id"
              filter-operator="eq"
              @onSelect="setCustomFilter('project_sample_type', $event); fetchTestPackages()"
            />
          </b-col>
          <b-col class="p-0">
            <select-filter
              variant="info"
              icon="fa-box"
              placeholder="All Packages"
              :collection="test_package_filters"
              value-key="id"
              text-key="name"
              filter-field="submissions.test_package_id"
              filter-operator="eq"
              :disabled="!customFilters.project_sample_type"
              @onSelect="setCustomFilter('project_test_package', $event)"
            />
          </b-col>
          <b-col>
            <select-filter
              variant="info"
              icon="fa-flag"
              placeholder="All Statuses"
              :collection="project_status_filters"
              value-key="id"
              text-key="status"
              filter-field="projectStatusLogs.project_status_id"
              filter-operator="eq"
              @onSelect="setCustomFilter('project_project_status', $event)"
            />
          </b-col>
        </b-row>
      </div>
      <span slot="pid" slot-scope="{ row }">
        <router-link :to="`/projects/${row.id}`">
          <b>{{ row.publicNumber() }}</b>
        </router-link>
      </span>
      <span slot="customer.full_name" slot-scope="{ row }">
        <existence-gate :entity="row" :levels="['customer']" :field="'full_name'">
          <template #existing-content>
            <router-link
              :to="`/customer/${getData(row, {
                levels: ['customer'],
                field: 'id',
              })}`"
            >
              {{ getData(row, { levels: ["customer"], field: "full_name" }) }}
            </router-link>
          </template>
        </existence-gate>
      </span>
      <span slot="customer_contact" slot-scope="{ row }">
        <existence-gate
          :entity="row"
          :levels="['customer_contact']"
          :field="'name'"
        >
          <template #existing-content>
            {{ getData(row, { levels: ["customer_contact"], field: "name" }) }}
          </template>
        </existence-gate>
      </span>
      <span slot="customer_location" slot-scope="{ row }">
        <existence-gate
          :entity="row"
          :levels="['customer_location']"
          :field="'name'"
        >
          <template #existing-content>
            {{ getData(row, { levels: ["customer_location"], field: "name" }) }}
          </template>
        </existence-gate>
      </span>
      <span
        v-if="row.submissions.length"
        slot="sample_types"
        slot-scope="{ row }"
      >
        {{ getProjectData(row, "sample_types") }}
      </span>
      <span
        v-if="row.submissions.length"
        slot="test_packages"
        slot-scope="{ row }"
      >
        {{ getProjectData(row, "test_packages") }}
      </span>
      <span
        v-if="row.submissions.length"
        slot="turnarounds"
        slot-scope="{ row }"
      >
        {{ getProjectData(row, "turnarounds") }}
      </span>
      <span
        v-if="row.submissions.length"
        slot="submission_count"
        slot-scope="{ row }"
      >
        {{ getProjectData(row, "sample_count") }}
      </span>
      <span slot="created_at" slot-scope="{ row }" v-if="row && row.created_at">
        {{ row.created_at | moment("L") }}
      </span>
      <span slot="received_percent" slot-scope="{ row }">
        <project-progress-badge :project="row" status_name="Received" />
      </span>
      <span slot="prepped_percent" slot-scope="{ row }">
        <project-progress-badge :project="row" status_name="Prepped" />
      </span>
      <span slot="scheduled_percent" slot-scope="{ row }">
        <project-progress-badge :project="row" status_name="Scheduled" />
      </span>
      <span slot="results_percent" slot-scope="{ row }">
        <project-progress-badge :project="row" status_name="Complete" />
      </span>
      <span
        slot="paid_at"
        slot-scope="{ row }"
        v-if="row && row.id && getPaidAt(row)"
      >
        {{ getPaidAt(row) | moment("L") }}
      </span>
      <span slot="quality_control" slot-scope="{ row }">
        {{ row.quality_control | moment("L") }}
      </span>
    </v-server-table>
  </b-overlay>
</template>

<script>
import Vue from "vue";
import ProjectProgressBadge from "./ProjectProgressBadge";
import BaseServerTable from "@/components/shared/BaseServerTable";
import ExistenceGate from "@/components/shared/ExistenceGate";
import SelectFilter from "@/components/shared/SelectFilter";
import ProjectCollection from "@/collections/lab/ProjectCollection";
import { ServerTable } from "vue-tables-2";
import { getCollection } from "@/shared/fetch-utils";
import { getEntityData } from "@/shared/utils";

Vue.use(ServerTable);

export default {
  name: "ProjectServerTable",
  extends: BaseServerTable,
  components: {
    ExistenceGate,
    ProjectProgressBadge,
    SelectFilter,
    ServerTable,
  },
  props: {
    collection: {
      type: Object,
      default: () => new ProjectCollection(),
    },
    project_statuses: {
      type: Object,
      default: () => null,
    },
    sample_types: {
      type: Object,
      default: () => null,
    },
    test_packages: {
      type: Object,
      default: () => null,
    },
    show_columns: {
      type: Array,
      default: () => [
        "pid",
        "customer.full_name",
        "sample_types",
        "test_packages",
        "turnarounds",
        "submission_count",
        "created_at",
        "received_percent",
        "prepped_percent",
        "scheduled_percent",
        "results_percent",
        "paid_at",
        "quality_control"
      ],
    },
    filterable: {
      type: Array,
      default: () => ["searchable"],
    },
    sortable: {
      type: Array,
      default: () => ["pid", "customer.full_name", "created_at"],
    },
  },
  data() {
    return {
      customer_id: this.$route.params.customer_id || null,
      test_package_id: this.$route.params.test_package_id || null,
      project_status_collection: null,
      sample_type_collection: null,
      test_package_collection: null,
      table_config: this.lodash.merge(this.getDefaultTableConfig(), {
        options: {
          headings: {
            pid: "#",
            "customer.full_name": "Customer",
            sample_types: "Type(s)",
            test_packages: "Package(s)",
            turnarounds: "Turnaround(s)",
            submission_count: "Samples",
            "current_project_status_log.project_status.status": "Status",
            created_at: "Submitted",
            received_percent: "Received",
            prepped_percent: "Prepped",
            scheduled_percent: "Scheduled",
            results_percent: "Results",
            paid_at: "Paid At",
            quality_control: "QC",
            actions: "Actions",
          },
          filterable: this.filterable,
          sortable: this.sortable,
        },
      }),
      show_quality_control: 0
    };
  },
  computed: {
    project_status_filters() {
      return this.project_statuses || this.project_status_collection;
    },
    sample_type_filters() {
      return this.sample_types || this.sample_type_collection;
    },
    test_package_filters() {
      return this.test_packages || this.test_package_collection;
    },
  },
  methods: {
    getData({ _reference }, data) {
      return getEntityData({
        entity: _reference,
        ...data,
      });
    },
    getProjectData(project, data_type) {
      const submissions = project.getSubmissions().models;
      let data = null;

      if (submissions.length) {
        switch (data_type) {
          case "pid":
            data = project.publicNumber();
            break;
          case "sample_types":
            data = Array.from(new Set(submissions.map((submission) => {
              return submission.getSampleType().name;
            })));
            break;
          case "test_packages":
            data = Array.from(new Set(submissions.map((submission) => {
              return submission.getTestPackage().name;
            })));
            break;
          case "turnarounds":
            data = submissions.map((submission) => {
              return (
                submission.turnaround_id && submission.getTurnaround().name
              );
            });
            break;
          case "sample_count":
            data = submissions.reduce((acc, cur) => {
              return acc + cur.getSamples().models.length;
            }, 0);
            break;
          default:
        }
      }

      return data && typeof data === "object"
        ? data
            .filter((item) => item)
            .sort()
            .join(", ")
        : data;
    },
    getPaidAt(project) {
      let log_created_at = null;

      project.getProjectStatusLogs().each((log) => {
        let status = log.getProjectStatus();
        if (status.status === "Paid") {
          log_created_at = log.created_at;
        }
      });

      return log_created_at;
    },
    async init() {
      const payload = {
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        limit: "all",
        message: this.$bvToast,
      };

      !this.project_statuses &&
        (this.project_status_collection = await getCollection({
          collection_type: "project_status",
          limit: "all",
          message: this.$bvToast,
        }));
      !this.sample_types &&
        (this.sample_type_collection = await getCollection({
          ...payload,
          collection_type: "sample_type",
        }));
    },
    async fetchTestPackages() {
      if (this.test_packages) return
      this.test_package_collection = null

      let filters = []

      if (this.customFilters.project_sample_type) {
        filters.push({
          logic: "filterEquals",
          field: "sample_type_id",
          value: this.customFilters.project_sample_type.value,
        })
      }

      this.test_package_collection = await getCollection({
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        filters,
        limit: "all",
        message: this.$bvToast,
        collection_type: "test_package",
      })
    },
    prepareCollectionFilter(filter) {
      filter.sortAddDesc("pid", true);

      this.customer_id && filter.filterEquals("customer_id", this.customer_id);

      filter.filterEquals("laboratory_id", this.$store.getters.getLaboratoryContext.id);

      this.test_package_id &&
        filter.filterEquals(
          "submissions.test_package_id",
          this.test_package_id
        );

      return filter;
    },
  },
  created() {
    this.init();

    this.toggleColumn("quality_control", this.show_quality_control);
  },
};
</script>
